import {
  Copy,
  Link,
  Rocket,
  Sparkles,
  UserMinus,
  Users,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type { Game } from "../../data/games";
import { simulatedParticipants } from "../../data/simulatedParticipants";
import { createInviteLink } from "../../game-engine/sessionCode";
import type { SimulatedParticipant } from "../../game-engine/types";
import { ParticipantAvatar } from "../ParticipantAvatar/ParticipantAvatar";
import styles from "./GameLobby.module.css";

interface GameLobbyProps {
  game: Game;
  code: string;
  participants: readonly SimulatedParticipant[];
  onJoinAll: () => void;
  onParticipantLeave: (participantId: string) => void;
  onStart: () => void;
  /** Hide the "Anslut alla nu" button (live mode: phones join themselves). */
  hideJoinAll?: boolean;
}

type CopyKind = "code" | "link";
type ParticipantPresence = "present" | "leaving";

interface PresenceParticipant {
  participant: SimulatedParticipant;
  presence: ParticipantPresence;
  slot: number;
}

const participantLeaveDuration = 380;
const mobileLobbyQuery = "(max-width: 55.999rem)";

function getParticipantSlot(
  participant: SimulatedParticipant,
  fallbackIndex: number,
) {
  const simulatedSlot = simulatedParticipants.findIndex(
    (candidate) => candidate.id === participant.id,
  );

  return simulatedSlot >= 0 ? simulatedSlot : fallbackIndex;
}

function usePresenceParticipants(
  participants: readonly SimulatedParticipant[],
) {
  const [renderedParticipants, setRenderedParticipants] = useState<
    PresenceParticipant[]
  >(() =>
    participants.map((participant, index) => ({
      participant,
      presence: "present",
      slot: getParticipantSlot(participant, index),
    })),
  );
  const previousParticipantsRef = useRef(participants);
  const latestParticipantIdsRef = useRef(
    new Set(participants.map((participant) => participant.id)),
  );
  const leaveTimersRef = useRef(new Map<string, number>());

  useEffect(() => {
    const nextParticipantIds = new Set(
      participants.map((participant) => participant.id),
    );
    latestParticipantIdsRef.current = nextParticipantIds;

    participants.forEach((participant) => {
      const leaveTimer = leaveTimersRef.current.get(participant.id);

      if (leaveTimer !== undefined) {
        window.clearTimeout(leaveTimer);
        leaveTimersRef.current.delete(participant.id);
      }
    });

    setRenderedParticipants((currentParticipants) => {
      const currentById = new Map(
        currentParticipants.map((entry) => [entry.participant.id, entry]),
      );
      const presentParticipants = participants.map((participant, index) => ({
        participant,
        presence: "present" as const,
        slot:
          currentById.get(participant.id)?.slot ??
          getParticipantSlot(participant, index),
      }));
      const leavingParticipants = currentParticipants
        .filter((entry) => !nextParticipantIds.has(entry.participant.id))
        .map((entry) => ({ ...entry, presence: "leaving" as const }));

      return [...presentParticipants, ...leavingParticipants];
    });

    previousParticipantsRef.current
      .filter((participant) => !nextParticipantIds.has(participant.id))
      .forEach((participant) => {
        if (leaveTimersRef.current.has(participant.id)) {
          return;
        }

        const leaveTimer = window.setTimeout(() => {
          if (!latestParticipantIdsRef.current.has(participant.id)) {
            setRenderedParticipants((currentParticipants) =>
              currentParticipants.filter(
                (entry) => entry.participant.id !== participant.id,
              ),
            );
          }
          leaveTimersRef.current.delete(participant.id);
        }, participantLeaveDuration);

        leaveTimersRef.current.set(participant.id, leaveTimer);
      });

    previousParticipantsRef.current = participants;
  }, [participants]);

  useEffect(
    () => () => {
      leaveTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    },
    [],
  );

  return renderedParticipants;
}

function useIsMobileLobby() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia?.(mobileLobbyQuery);

    if (!mediaQuery) {
      return;
    }

    const updateMatch = () => setIsMobile(mediaQuery.matches);
    updateMatch();
    mediaQuery.addEventListener?.("change", updateMatch);

    return () => mediaQuery.removeEventListener?.("change", updateMatch);
  }, []);

  return isMobile;
}

export function GameLobby({
  game,
  code,
  participants,
  onJoinAll,
  onParticipantLeave,
  onStart,
  hideJoinAll = false,
}: GameLobbyProps) {
  const [copyStatus, setCopyStatus] = useState("");
  const [joinAnnouncement, setJoinAnnouncement] = useState("");
  const [isPlayerSheetOpen, setIsPlayerSheetOpen] = useState(false);
  const isMobileLobby = useIsMobileLobby();
  const renderedParticipants = usePresenceParticipants(participants);
  const previousParticipantsRef = useRef(participants);
  const playerSheetRef = useRef<HTMLElement>(null);
  const playerSheetCloseRef = useRef<HTMLButtonElement>(null);
  const playerSheetTriggerRef = useRef<HTMLButtonElement>(null);
  const phonesNeeded = Math.max(0, game.minPlayers - 1);
  const canStart = participants.length >= phonesNeeded;
  const allJoined = participants.length === simulatedParticipants.length;
  const inviteLink = createInviteLink(code, window.location.origin);

  useEffect(() => {
    const previousParticipants = previousParticipantsRef.current;
    const joinedParticipant = participants.find(
      (participant) =>
        !previousParticipants.some(
          (previous) => previous.id === participant.id,
        ),
    );
    const departedParticipant = previousParticipants.find(
      (participant) =>
        !participants.some((current) => current.id === participant.id),
    );

    if (joinedParticipant) {
      setJoinAnnouncement(`${joinedParticipant.name} anslöt till spelet.`);
    } else if (departedParticipant) {
      setJoinAnnouncement(`${departedParticipant.name} lämnade spelet.`);
    }

    previousParticipantsRef.current = participants;
  }, [participants]);

  const closePlayerSheet = useCallback(() => {
    setIsPlayerSheetOpen(false);
    requestAnimationFrame(() => playerSheetTriggerRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!isMobileLobby) {
      setIsPlayerSheetOpen(false);
      return;
    }

    if (!isPlayerSheetOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closePlayerSheet();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusableElements = Array.from(
        playerSheetRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );
      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement = focusableElements.at(-1);

      if (!firstFocusableElement || !lastFocusableElement) {
        event.preventDefault();
        return;
      }

      if (
        event.shiftKey &&
        (document.activeElement === firstFocusableElement ||
          !playerSheetRef.current?.contains(document.activeElement))
      ) {
        event.preventDefault();
        lastFocusableElement.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === lastFocusableElement ||
          !playerSheetRef.current?.contains(document.activeElement))
      ) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    const focusFrame = requestAnimationFrame(() =>
      playerSheetCloseRef.current?.focus(),
    );

    return () => {
      cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closePlayerSheet, isMobileLobby, isPlayerSheetOpen]);

  const copyInvite = async (kind: CopyKind) => {
    const value = kind === "code" ? code : inviteLink;

    try {
      if (!navigator.clipboard) {
        throw new Error("Clipboard API saknas");
      }

      await navigator.clipboard.writeText(value);
      setCopyStatus(
        kind === "code" ? "Spelkoden är kopierad." : "Inbjudningslänken är kopierad.",
      );
    } catch {
      setCopyStatus(
        "Det gick inte att kopiera automatiskt. Markera koden och kopiera den manuellt.",
      );
    }
  };

  return (
    <main className={styles.lobby}>
      <section className={styles.invitePanel} aria-labelledby="lobby-heading">
        <span className={styles.eyebrow}>Värdlobby · {game.title}</span>
        <h1 id="lobby-heading" data-session-heading tabIndex={-1}>
          Samla gänget
        </h1>
        <p className={styles.intro}>
          Dela koden eller länken. Deltagarna dyker upp här så fort de har
          anslutit.
        </p>

        <div className={styles.codeCard}>
          <span className={styles.codeLabel}>Spelkod</span>
          <output className={styles.code} aria-label={`Spelkod ${code}`}>
            {code}
          </output>
          <span className={styles.codeHint}>partycast.app/join</span>
        </div>

        <div className={styles.copyActions}>
          <button type="button" onClick={() => copyInvite("code")}>
            <Copy aria-hidden="true" />
            Kopiera kod
          </button>
          <button type="button" onClick={() => copyInvite("link")}>
            <Link aria-hidden="true" />
            Kopiera länk
          </button>
        </div>

        <p className={styles.copyStatus} aria-live="polite" aria-atomic="true">
          {copyStatus}
        </p>

        <div className={styles.lobbyActions}>
          <button
            className={styles.startButton}
            type="button"
            disabled={!canStart}
            onClick={onStart}
          >
            <Rocket aria-hidden="true" />
            Starta spelet
          </button>
          {!hideJoinAll && !allJoined ? (
            <button
              className={styles.joinAllButton}
              type="button"
              onClick={onJoinAll}
            >
              <Sparkles aria-hidden="true" />
              Anslut alla nu
            </button>
          ) : null}
        </div>

        <p className={styles.startHint}>
          {canStart
            ? "Ni är tillräckligt många för att börja."
            : phonesNeeded === 1
              ? "Väntar på minst 1 telefon."
              : `Väntar på minst ${phonesNeeded} telefoner.`}
        </p>
      </section>

      <button
        className={styles.mobilePlayerTrigger}
        ref={playerSheetTriggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={isPlayerSheetOpen}
        aria-controls="mobile-player-sheet"
        onClick={() => setIsPlayerSheetOpen(true)}
      >
        <span className={styles.triggerAvatars} aria-hidden="true">
          {participants.slice(0, 3).map((participant) => (
            <ParticipantAvatar
              key={participant.id}
              participant={participant}
              size="small"
              showName={false}
            />
          ))}
        </span>
        <span>
          <strong key={participants.length}>{participants.length}</strong>
          {participants.length === 1 ? " spelare ansluten" : " spelare anslutna"}
        </span>
        <Users aria-hidden="true" />
      </button>

      <button
        className={styles.sheetBackdrop}
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        aria-label="Stäng spelarlistan"
        data-open={isPlayerSheetOpen ? "true" : undefined}
        onClick={closePlayerSheet}
      />

      <section
        id="mobile-player-sheet"
        className={styles.participantStage}
        ref={playerSheetRef}
        aria-labelledby="participants-heading"
        role={isMobileLobby ? "dialog" : undefined}
        aria-modal={isMobileLobby ? "true" : undefined}
        aria-hidden={
          isMobileLobby && !isPlayerSheetOpen ? "true" : undefined
        }
        inert={isMobileLobby && !isPlayerSheetOpen ? true : undefined}
        data-mobile-open={isPlayerSheetOpen ? "true" : undefined}
      >
        <span className={styles.sheetHandle} aria-hidden="true" />
        <div className={styles.stageHeading}>
          <div>
            <span className={styles.stageEyebrow}>Live från lobbyn</span>
            <h2 id="participants-heading">Spelare</h2>
          </div>
          <span className={styles.playerCount}>
            <Users aria-hidden="true" />
            {participants.length} av {game.maxPlayers}
          </span>
          <button
            className={styles.sheetClose}
            ref={playerSheetCloseRef}
            type="button"
            aria-label="Stäng spelarlistan"
            onClick={closePlayerSheet}
          >
            <X aria-hidden="true" />
          </button>
        </div>

        <div className={styles.avatarField}>
          <span className={styles.decorativeRing} aria-hidden="true" />
          <ul className={styles.avatarList}>
            {renderedParticipants.map(({ participant, presence, slot }) => (
              <li
                key={participant.id}
                data-slot={slot}
                data-presence={presence}
              >
                <ParticipantAvatar
                  participant={participant}
                  size="large"
                />
                <button
                  className={styles.removeParticipant}
                  type="button"
                  disabled={presence === "leaving"}
                  aria-label={`Ta bort ${participant.name}`}
                  onClick={() => onParticipantLeave(participant.id)}
                >
                  <UserMinus aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>

          {participants.length === 0 ? (
            <div className={styles.waiting}>
              <span aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
              Väntar på första spelaren…
            </div>
          ) : null}
        </div>

        <p className={styles.joinAnnouncement} aria-live="polite">
          {joinAnnouncement}
        </p>
      </section>
    </main>
  );
}
